import { useActionState, useRef } from "react"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { createUpgradeSession } from "@/lib/billing-api"
import { formatBillingPrice } from "@/lib/billing-format"

const UPGRADE_ERROR = "Upgrade is unavailable. Refresh billing status and try again."

function blockedMessage(subscription) {
  if (subscription.cancel_at_period_end) {
    return "Reverse the scheduled cancellation before upgrading."
  }
  if (subscription.status === "trialing") {
    return "Upgrades become available after your trial ends."
  }
  if (subscription.status === "past_due") {
    return "Resolve the outstanding payment before upgrading."
  }
  if (subscription.status !== "active") {
    return "Upgrades are unavailable for this subscription."
  }
  return null
}

function higherPlans(plans, tier) {
  const currentIndex = plans.findIndex((plan) => plan.key === tier)
  return currentIndex < 0 ? [] : plans.slice(currentIndex + 1)
}

export function UpgradeOptions({
  plans,
  subscription,
  csrfToken,
  redirectTo,
  plansLoadFailed,
  plansLoading,
  onRetryPlans,
}) {
  const attemptInFlightRef = useRef(false)
  const [error, action, pending] = useActionState(
    async (previous, formData) => {
      if (attemptInFlightRef.current) return previous

      attemptInFlightRef.current = true
      const attemptId = globalThis.crypto.randomUUID()

      try {
        const { url } = await createUpgradeSession(formData.get("tier"), {
          csrfToken,
          attemptId,
        })
        redirectTo(url)
        return null
      } catch {
        attemptInFlightRef.current = false
        return UPGRADE_ERROR
      }
    },
    null,
  )
  const blocked = blockedMessage(subscription)
  const targets = blocked ? [] : higherPlans(plans, subscription.tier)

  return (
    <section className="space-y-4" aria-label="Upgrade plan">
      <h2 className="text-xl font-semibold">Upgrade plan</h2>
      {blocked ? <p className="text-sm text-muted-foreground">{blocked}</p> : null}
      {!blocked && plansLoadFailed ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Upgrade options could not be loaded.</p>
          <Button type="button" variant="outline" disabled={plansLoading} onClick={onRetryPlans}>
            {plansLoading ? "Loading…" : "Retry upgrade options"}
          </Button>
        </div>
      ) : null}
      {!blocked && !plansLoadFailed && targets.length === 0 ? (
        <p className="text-sm text-muted-foreground">You are on the highest monthly plan.</p>
      ) : null}
      {!blocked && !plansLoadFailed && targets.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2">
          {targets.map((plan) => (
            <Card key={plan.key}>
              <CardHeader>
                <CardTitle>{plan.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold">
                  {formatBillingPrice(plan)}
                  <span className="text-sm font-normal text-muted-foreground">/mo</span>
                </p>
              </CardContent>
              <CardFooter>
                <form action={action} className="w-full">
                  <input type="hidden" name="tier" value={plan.key} />
                  <Button type="submit" className="w-full" disabled={pending}>
                    {pending ? "Opening…" : `Upgrade to ${plan.name}`}
                  </Button>
                </form>
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : null}
      {error ? <p className="text-sm text-destructive" role="alert">{error}</p> : null}
    </section>
  )
}
